import { z } from "zod";
import type {
  BoundedStat,
  MhCharacter,
  MhRelation,
  MonsterIndividual,
} from "./types";

const boundedStatSchema = z
  .object({ current: z.number(), min: z.number(), max: z.number() })
  .transform((value) =>
    normalizeBoundedStat(value.current, value.min, value.max),
  );

const relationSchema = z.object({
  targetId: z.string().min(1),
  label: z.string(),
  affinity: boundedStatSchema,
  notes: z.string(),
  knowsTargetName: z.boolean(),
});

export const mhCharacterSchema: z.ZodType<MhCharacter> = z.object({
  id: z.string().regex(/^C\d+$/),
  identity: z.object({
    name: z.string().min(1),
    pronouns: z.string(),
    race: z.string(),
    occupation: z.string(),
    affiliation: z.string(),
    title: z.string(),
  }),
  hunter: z.object({
    hasLicense: z.boolean(),
    hr: z.number().int().min(1).max(9).nullable(),
  }),
  presence: z.object({
    status: z.enum(["present", "offscene", "departed"]),
    location: z.string(),
    lastSeenAt: z.string(),
  }),
  appearance: z.string(),
  status: z.object({
    current: z.string(),
    health: z.number().min(0).max(100).nullable(),
    stamina: z.number().min(0).max(100).nullable(),
  }),
  relations: z.array(relationSchema),
  bio: z.object({
    background: z.string(),
    biography: z.string(),
    recentEvents: z.array(z.string()),
    currentMotive: z.string(),
    shortTermGoal: z.string(),
    longTermGoal: z.string(),
  }),
  legacy: z.record(z.string(), z.unknown()).optional(),
});

export const monsterIndividualSchema: z.ZodType<MonsterIndividual> = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  speciesId: z.string().min(1),
  individualName: z.string(),
  health: z.number().min(0).max(100),
  rage: z.number().min(0).max(100),
  fatigue: z.number().min(0).max(100),
  parts: z.record(
    z.string(),
    z.object({ durability: z.number().min(0).max(100), broken: z.boolean() }),
  ),
  tailSevered: z.boolean(),
  location: z.string(),
  scars: z.array(z.string()),
  territory: z.array(z.string()),
  lastSeenAt: z.string(),
  status: z.enum(["active", "fled", "captured", "slain", "unknown"]),
  legacy: z.record(z.string(), z.unknown()).optional(),
});

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function text(value: unknown, fallback = "") {
  return typeof value === "string" ? value.trim() : fallback;
}

function numberOrNull(value: unknown, min: number, max: number) {
  return typeof value === "number" && Number.isFinite(value)
    ? Math.max(min, Math.min(max, value))
    : null;
}

export function normalizeBoundedStat(
  current: unknown,
  min = -100,
  max = 100,
): BoundedStat {
  const safeMin = Number.isFinite(min) ? min : -100;
  const safeMax = Number.isFinite(max) && max >= safeMin ? max : 100;
  const numeric =
    typeof current === "number" && Number.isFinite(current) ? current : 0;
  return {
    current: Math.round(Math.max(safeMin, Math.min(safeMax, numeric))),
    min: safeMin,
    max: safeMax,
  };
}

function normalizeRelations(value: unknown): MhRelation[] {
  const values = Array.isArray(value) ? value : [];
  return values.flatMap((entry) => {
    const raw = record(entry);
    const targetId = text(raw.targetId ?? raw.target ?? raw.id);
    if (!targetId) return [];
    const rawAffinity = record(raw.affinity);
    return [
      {
        targetId,
        label: text(raw.label ?? raw.relation),
        affinity: normalizeBoundedStat(
          rawAffinity.current ?? raw.favorability ?? raw.affinity,
          -100,
          100,
        ),
        notes: text(raw.notes ?? raw.description),
        knowsTargetName: Boolean(raw.knowsTargetName ?? true),
      },
    ];
  });
}

export function normalizeMhCharacter(
  id: string,
  value: unknown,
  currentLocation = "",
): MhCharacter {
  const parsed = mhCharacterSchema.safeParse(value);
  if (parsed.success) return parsed.data;
  const raw = record(value);
  const identity = record(raw.identity);
  const hunter = record(raw.hunter);
  const presence = record(raw.presence);
  const bio = record(raw.bio);
  const rawLocation = text(presence.location ?? raw.location, currentLocation);
  const oldStatus = text(presence.status ?? raw.status).toLowerCase();
  const status: MhCharacter["presence"]["status"] =
    oldStatus === "departed" || oldStatus === "dead"
      ? "departed"
      : oldStatus === "present" || rawLocation === currentLocation
        ? "present"
        : "offscene";
  const relations = normalizeRelations(
    raw.relations ?? bio.rawRelations ?? raw.rawRelations,
  );
  if (!relations.length && typeof raw.favorability === "number") {
    relations.push({
      targetId: "B1",
      label: "",
      affinity: normalizeBoundedStat(raw.favorability),
      notes: "由旧版好感度字段迁移",
      knowsTargetName: true,
    });
  }
  const recentSource = bio.recentEvents ?? raw.recentEvents;
  const recentEvents = Array.isArray(recentSource)
    ? recentSource.map((item: unknown) => text(item)).filter(Boolean)
    : [];
  return mhCharacterSchema.parse({
    id,
    identity: {
      name: text(identity.name ?? raw.name, id),
      pronouns: text(identity.pronouns ?? raw.pronouns, "未指定"),
      race: text(identity.race ?? raw.race, "未知"),
      occupation: text(
        identity.occupation ?? raw.occupation ?? raw.role,
        "未记录",
      ),
      affiliation: text(identity.affiliation ?? raw.affiliation),
      title: text(identity.title ?? raw.title),
    },
    hunter: {
      hasLicense: Boolean(
        hunter.hasLicense ?? raw.hasHunterLicense ?? hunter.hr,
      ),
      hr: numberOrNull(hunter.hr ?? raw.hr, 1, 9),
    },
    presence: {
      status,
      location: rawLocation,
      lastSeenAt: text(presence.lastSeenAt ?? raw.lastSeenAt),
    },
    appearance: text(raw.appearance ?? identity.appearance),
    status: {
      current: text(record(raw.status).current ?? raw.status, "状态未记录"),
      health: numberOrNull(record(raw.status).health ?? raw.health, 0, 100),
      stamina: numberOrNull(record(raw.status).stamina ?? raw.stamina, 0, 100),
    },
    relations,
    bio: {
      background: text(bio.background ?? raw.background),
      biography: text(bio.biography ?? raw.biography),
      recentEvents,
      currentMotive: text(bio.currentMotive ?? raw.currentMotive),
      shortTermGoal: text(bio.shortTermGoal ?? raw.shortTermGoal),
      longTermGoal: text(bio.longTermGoal ?? raw.longTermGoal),
    },
    legacy: raw,
  });
}

export function normalizeMonsterIndividual(
  id: string,
  value: unknown,
): MonsterIndividual {
  const parsed = monsterIndividualSchema.safeParse(value);
  if (parsed.success) return parsed.data;
  const raw = record(value);
  const rawParts = record(raw.parts);
  const parts = Object.fromEntries(
    Object.entries(rawParts).map(([part, state]) => {
      const data = record(state);
      return [
        part,
        {
          durability: numberOrNull(data.durability, 0, 100) ?? 100,
          broken: Boolean(data.broken),
        },
      ];
    }),
  );
  if (!Object.keys(parts).length)
    Object.assign(parts, {
      head: { durability: 100, broken: false },
      body: { durability: 100, broken: false },
      tail: { durability: 100, broken: false },
    });
  return monsterIndividualSchema.parse({
    id,
    name: text(raw.name, id),
    speciesId: text(raw.speciesId, id),
    individualName: text(raw.individualName),
    health: numberOrNull(raw.health, 0, 100) ?? 100,
    rage: numberOrNull(raw.rage, 0, 100) ?? 0,
    fatigue: numberOrNull(raw.fatigue, 0, 100) ?? 0,
    parts,
    tailSevered: Boolean(raw.tailSevered),
    location: text(raw.location),
    scars: Array.isArray(raw.scars)
      ? raw.scars.map((item) => text(item)).filter(Boolean)
      : [],
    territory: Array.isArray(raw.territory)
      ? raw.territory.map((item) => text(item)).filter(Boolean)
      : [],
    lastSeenAt: text(raw.lastSeenAt),
    status: ["active", "fled", "captured", "slain"].includes(text(raw.status))
      ? text(raw.status)
      : "unknown",
    legacy: raw,
  });
}

export function affinityTowardsPlayer(character: MhCharacter) {
  return (
    character.relations.find((relation) => relation.targetId === "B1")?.affinity
      .current ?? 0
  );
}
